import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CardContent,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { authApi } from '../services/api';
import LegalBusinessDetails from './LegalBusinessDetails';

const VerifyEmail = () => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('Verifying your email address...');
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setSuccess(false);
      setMessage('This verification link is missing the required token.');
      setLoading(false);
      return;
    }

    let isMounted = true;

    const verify = async () => {
      try {
        const response = await authApi.verifyEmail(token);
        if (!isMounted) {
          return;
        }
        setSuccess(true);
        setMessage(response.data?.message || 'Your email address has been verified. You can sign in now.');
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setSuccess(false);
        setMessage(error.response?.data?.error || 'We could not verify this email link. Request a new link and try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, [location.search]);

  return (
    <>
      <Helmet>
        <title>Verify Email — Struxor Construction Software</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: 'url(/background_image.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: theme.palette.mode === 'light'
              ? 'linear-gradient(180deg, rgba(17,24,39,0.45) 0%, rgba(17,24,39,0.6) 60%, rgba(17,24,39,0.75) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.8) 60%, rgba(0,0,0,0.9) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
          <Paper elevation={24} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box
              sx={{
                background: theme.palette.mode === 'light'
                  ? 'linear-gradient(45deg, #f8f9fa, #e9ecef)'
                  : `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.default, 0.8)})`,
                color: 'text.primary',
                textAlign: 'center',
                py: 4,
                px: 3,
              }}
            >
              <Box
                component="img"
                src="/struxor.png"
                alt="Struxor logo"
                sx={{
                  height: 56,
                  mb: 1.5,
                  filter: theme.palette.mode === 'dark'
                    ? 'brightness(0) invert(1) drop-shadow(0 2px 2px rgba(0,0,0,0.3))'
                    : 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
                }}
              />
              <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
                Email Verification
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Finish setting up your Struxor account
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {loading ? (
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                ) : success ? (
                  <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
                ) : (
                  <ErrorOutline color="error" sx={{ fontSize: 56, mb: 2 }} />
                )}
                <Typography variant="h6" gutterBottom>
                  {loading ? 'Verifying your email' : success ? 'Email verified' : 'Verification failed'}
                </Typography>
              </Box>

              <Alert severity={loading ? 'info' : success ? 'success' : 'error'} sx={{ mb: 3, borderRadius: 2 }}>
                {message}
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/login')}
                  disabled={loading}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
                >
                  Go to Sign In
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate('/register')}
                  disabled={loading}
                  sx={{ borderRadius: 2, fontWeight: 500 }}
                >
                  Back to Register
                </Button>
              </Box>
            </CardContent>
          </Paper>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <LegalBusinessDetails
              compact
              textAlign="center"
              textColor="rgba(255, 255, 255, 0.82)"
              linkColor="rgba(255, 255, 255, 0.95)"
              showLegalLinks
            />
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default VerifyEmail;